import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faPen,
  faCheck,
  faXmark,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faMusic,
  faWandMagicSparkles,
  faListUl,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';

const server = axios.create({ baseURL: '/api' });

function authHeaders() {
  const token = localStorage.getItem('walmates-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function userId() {
  return localStorage.getItem('walmates-user_id');
}

/** Teal-forward gradients for personality avatars. */
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #14b8a6, #0d9488)',
  'linear-gradient(135deg, #0d9488, #0891b2)',
  'linear-gradient(135deg, #2dd4bf, #14b8a6)',
  'linear-gradient(135deg, #14b8a6, #059669)',
  'linear-gradient(135deg, #06b6d4, #14b8a6)',
  'linear-gradient(135deg, #0f766e, #14b8a6)',
];

function avatarGradient(personaId) {
  return AVATAR_GRADIENTS[Math.abs(Number(personaId) || 0) % AVATAR_GRADIENTS.length];
}

function PersonaRow({
  persona,
  isActive,
  appendMode = false,
  onPlay,
  onRename,
  onOpenDetail,
  onDelete,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(persona.name);

  useEffect(() => {
    setDraft(persona.name);
  }, [persona.name]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== persona.name) onRename(persona.id, trimmed);
    else setDraft(persona.name);
  };

  const meta = [
    persona.top_artists?.length ? persona.top_artists.slice(0, 3).join(' · ') : null,
    persona.track_count ? `${persona.track_count} track${persona.track_count === 1 ? '' : 's'}` : 'No tracks yet',
  ].filter(Boolean).join(' — ');

  return (
    <li className={['music-persona-row', 'music-queue-source-row', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}>
      <div className="music-playlist-thumb-wrap">
        <button
          type="button"
          className="music-persona-avatar music-playlist-thumb"
          style={{ background: avatarGradient(persona.id) }}
          onClick={() => (persona.track_count > 0 ? onPlay(persona) : onOpenDetail(persona))}
          aria-label={persona.track_count > 0
            ? (appendMode ? `Add ${persona.name} to queue` : `Play ${persona.name}`)
            : `Open ${persona.name}`}
        >
          <span className="music-persona-emoji" aria-hidden="true">
            {persona.emoji || '🎧'}
          </span>
        </button>
        {persona.track_count > 0 && (
          <span
            className={[
              'music-playlist-thumb-action',
              appendMode ? 'is-add' : 'is-play',
            ].join(' ')}
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
          </span>
        )}
      </div>

      <div
        className="music-persona-info music-playlist-meta-btn"
        role="button"
        tabIndex={0}
        onClick={() => { if (!editing) onOpenDetail(persona); }}
        onKeyDown={(e) => {
          if (!editing && (e.key === 'Enter' || e.key === ' ')) onOpenDetail(persona);
        }}
        aria-label={`View ${persona.name}`}
      >
        {editing ? (
          <div className="music-persona-edit" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={draft}
              maxLength={60}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') { setEditing(false); setDraft(persona.name); }
              }}
              aria-label="Personality name"
            />
            <button type="button" onClick={commit} aria-label="Save name">
              <FontAwesomeIcon icon={faCheck} />
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft(persona.name); }}
              aria-label="Cancel rename"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ) : (
          <div className="music-persona-name-line">
            <span className="music-persona-name">{persona.name}</span>
            <button
              type="button"
              className="music-persona-rename"
              onClick={(e) => { e.stopPropagation(); setEditing(true); }}
              aria-label={`Rename ${persona.name}`}
              title="Rename"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
            {isActive && <span className="music-persona-playing">In queue</span>}
          </div>
        )}
        {meta && <p className="music-persona-meta">{meta}</p>}
      </div>

      <button
        type="button"
        className="music-persona-delete"
        onClick={() => onDelete?.(persona)}
        aria-label={`Delete ${persona.name}`}
        title="Delete personality"
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>

      <button
        type="button"
        className="music-playlist-row-chevron"
        onClick={() => onOpenDetail(persona)}
        aria-label={`Open ${persona.name}`}
        title={`Open ${persona.name}`}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </li>
  );
}

function SuggestPane({ persona, suggestions, loading, onBack, onAddTrack, onPlayTrack }) {
  return (
    <div className="music-persona-detail">
      <div className="music-playlist-detail-nav">
        <button type="button" className="music-playlist-back" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{persona.name}</span>
        </button>
      </div>

      <div className="music-persona-detail-header">
        <div className="music-persona-detail-info">
          <h2>Suggested tracks</h2>
          <p>Matches this personality. Add ones you like — nothing is added automatically.</p>
        </div>
      </div>

      {loading ? (
        <div className="music-playlist-empty music-playlist-empty--compact">
          <p>Finding matches…</p>
        </div>
      ) : !suggestions?.length ? (
        <div className="music-playlist-empty music-playlist-empty--compact">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          <p>No suggestions right now. Add more tracks to this personality and try again.</p>
        </div>
      ) : (
        <ul className="music-persona-track-list">
          {suggestions.map((track, index) => {
            const thumb = track.thumbnail_medium || track.thumbnail_default || '';
            const title = track.suggested_title || track.title || 'Untitled';
            const artist = track.suggested_artist || track.artist || 'Unknown artist';
            return (
              <li key={track.video_id || index} className="music-persona-track-row">
                <span className="music-persona-track-index" aria-hidden="true">{index + 1}</span>
                <span className="music-persona-track-art">
                  {thumb ? <img src={thumb} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
                </span>
                <span className="music-persona-track-text">
                  <span className="music-persona-track-title">{title}</span>
                  <span className="music-persona-track-artist">
                    {artist}
                    {track.in_library ? ' · in library' : ' · YouTube'}
                  </span>
                </span>
                <div className="music-persona-track-actions">
                  {track.in_library ? (
                    <button
                      type="button"
                      className="music-persona-mini-btn is-primary"
                      onClick={() => onPlayTrack?.(track)}
                      title="Play"
                    >
                      <FontAwesomeIcon icon={faPlay} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="music-persona-mini-btn is-primary"
                      onClick={() => onAddTrack?.(track)}
                      title="Add to library"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AddFromLibraryPane({
  persona,
  playlists = [],
  allTracks = [],
  onBack,
  onAddVideoIds,
  onAddPlaylist,
}) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allTracks.slice(0, 40);
    return allTracks
      .filter((t) => (
        (t.title || '').toLowerCase().includes(q)
        || (t.artist || '').toLowerCase().includes(q)
      ))
      .slice(0, 40);
  }, [allTracks, query]);

  const usablePlaylists = (playlists || []).filter(
    (p) => p.trackIds?.length && String(p.name || '').toLowerCase() !== 'skipped'
  );

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="music-persona-detail">
      <div className="music-playlist-detail-nav">
        <button type="button" className="music-playlist-back" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{persona.name}</span>
        </button>
      </div>

      <div className="music-persona-detail-info">
        <h2>Add tracks</h2>
        <p>Add a playlist or pick tracks from your library.</p>
      </div>

      {usablePlaylists.length > 0 && (
        <>
          <p className="music-persona-detail-caption">Playlists</p>
          <ul className="music-persona-playlist-pick">
            {usablePlaylists.map((pl) => (
              <li key={pl.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => onAddPlaylist(pl.id))}
                >
                  <FontAwesomeIcon icon={faListUl} />
                  <span>{pl.name}</span>
                  <span className="music-persona-meta">{pl.trackIds?.length || 0}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="music-persona-detail-caption">Library</p>
      <input
        className="music-persona-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search tracks…"
        aria-label="Search library tracks"
      />
      <ul className="music-persona-track-list">
        {filtered.map((track) => {
          const thumb = track.thumbnail_medium || track.thumbnail_default || '';
          return (
            <li key={track.video_id} className="music-persona-track-row">
              <span className="music-persona-track-art">
                {thumb ? <img src={thumb} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
              </span>
              <span className="music-persona-track-text">
                <span className="music-persona-track-title">{track.title || 'Untitled'}</span>
                <span className="music-persona-track-artist">{track.artist || 'Unknown artist'}</span>
              </span>
              <button
                type="button"
                className="music-persona-mini-btn is-primary"
                disabled={busy}
                onClick={() => run(() => onAddVideoIds([track.video_id]))}
                title="Add to personality"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PersonaDetailPane({
  persona,
  playlists,
  allTracks,
  appendMode = false,
  onBack,
  onPlay,
  onClose,
  onRemoveTrack,
  onAddTracks,
  onSuggest,
  onPlayLibraryTrack,
  onAddSuggestedTrack,
}) {
  const [tracks, setTracks] = useState(null);
  const [pane, setPane] = useState('tracks'); // tracks | add | suggest
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const reloadTracks = () => {
    setTracks(null);
    server.get(`/music/personas/top_tracks?user_id=${userId()}&persona_id=${persona.id}&count=200`, {
      headers: authHeaders(),
    })
      .then((res) => setTracks(res.data?.tracks || []))
      .catch((err) => {
        console.error('Failed to load personality tracks:', err);
        setTracks([]);
      });
  };

  useEffect(() => {
    reloadTracks();
  }, [persona.id]);

  const handleSuggest = async () => {
    setPane('suggest');
    setSuggesting(true);
    setSuggestions(null);
    try {
      const tracks = await onSuggest?.(persona.id);
      setSuggestions(tracks || []);
    } catch (err) {
      console.error('Suggest failed:', err);
      setSuggestions([]);
    } finally {
      setSuggesting(false);
    }
  };

  if (pane === 'add') {
    return (
      <AddFromLibraryPane
        persona={persona}
        playlists={playlists}
        allTracks={allTracks}
        onBack={() => { setPane('tracks'); reloadTracks(); }}
        onAddVideoIds={async (videoIds) => {
          await onAddTracks?.(persona.id, { video_ids: videoIds });
        }}
        onAddPlaylist={async (playlistId) => {
          await onAddTracks?.(persona.id, { playlist_ids: [playlistId] });
        }}
      />
    );
  }

  if (pane === 'suggest') {
    return (
      <SuggestPane
        persona={persona}
        suggestions={suggestions}
        loading={suggesting}
        onBack={() => setPane('tracks')}
        onAddTrack={onAddSuggestedTrack}
        onPlayTrack={onPlayLibraryTrack}
      />
    );
  }

  return (
    <div className="music-persona-detail">
      <div className="music-playlist-detail-nav">
        <button type="button" className="music-playlist-back" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>Personalities</span>
        </button>
        <button type="button" className="music-playlist-sheet-done" onClick={onClose}>
          Done
        </button>
      </div>

      <div className="music-persona-detail-header">
        <div className="music-playlist-thumb-wrap">
          <div
            className="music-persona-avatar music-persona-avatar--large music-playlist-thumb"
            style={{ background: avatarGradient(persona.id) }}
            aria-hidden="true"
          >
            <span className="music-persona-emoji">{persona.emoji || '🎧'}</span>
          </div>
          {(tracks?.length || 0) > 0 && (
            <button
              type="button"
              className={[
                'music-playlist-thumb-action',
                appendMode ? 'is-add' : 'is-play',
              ].join(' ')}
              onClick={() => onPlay(persona)}
              aria-label={appendMode ? `Add ${persona.name} to queue` : `Play ${persona.name}`}
            >
              <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
            </button>
          )}
        </div>
        <div className="music-persona-detail-info">
          <h2>{persona.name}</h2>
          <p>{tracks?.length || 0} track{(tracks?.length || 0) === 1 ? '' : 's'} added</p>
        </div>
      </div>

      <div className="music-persona-detail-actions">
        <button type="button" className="music-persona-action-btn" onClick={() => setPane('add')}>
          <FontAwesomeIcon icon={faPlus} />
          <span>Add tracks</span>
        </button>
        <button
          type="button"
          className="music-persona-action-btn is-suggest"
          onClick={handleSuggest}
          disabled={!tracks?.length}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          <span>Suggest tracks</span>
        </button>
      </div>

      <p className="music-persona-detail-caption">Added tracks</p>

      {tracks === null ? (
        <div className="music-playlist-empty music-playlist-empty--compact">
          <p>Loading…</p>
        </div>
      ) : tracks.length === 0 ? (
        <div className="music-playlist-empty music-playlist-empty--compact">
          <FontAwesomeIcon icon={faMusic} />
          <p>Add playlists or tracks to shape this personality.</p>
        </div>
      ) : (
        <ul className="music-persona-track-list">
          {tracks.map((track, index) => {
            const thumb = track.thumbnail_medium || track.thumbnail_default || '';
            return (
              <li key={track.video_id || index} className="music-persona-track-row">
                <span className="music-persona-track-index" aria-hidden="true">{index + 1}</span>
                <span className="music-persona-track-art">
                  {thumb ? <img src={thumb} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
                </span>
                <span className="music-persona-track-text">
                  <span className="music-persona-track-title">{track.title || 'Untitled'}</span>
                  <span className="music-persona-track-artist">
                    {track.artist || 'Unknown artist'}
                  </span>
                </span>
                <button
                  type="button"
                  className="music-persona-mini-btn"
                  onClick={() => onRemoveTrack?.(persona.id, track.video_id).then(reloadTracks)}
                  title="Remove from personality"
                  aria-label={`Remove ${track.title}`}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const PersonaModal = ({
  open,
  personas = [],
  playlists = [],
  allTracks = [],
  activePersonaIds = [],
  appendMode = false,
  onPlayPersonas,
  onRenamePersona,
  onCreatePersona,
  onDeletePersona,
  onAddTracks,
  onRemoveTrack,
  onSuggestTracks,
  onPlayLibraryTrack,
  onAddSuggestedTrack,
  onClose,
}) => {
  const [detailPersonaId, setDetailPersonaId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setDetailPersonaId(null);
      setCreating(false);
      setNewName('');
    }
  }, [open]);

  const detailPersona = detailPersonaId == null
    ? null
    : personas.find((p) => p.id === detailPersonaId) || null;

  if (!open) return null;

  const playAndClose = (list) => {
    onPlayPersonas?.(list);
    onClose?.();
  };

  const submitCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const persona = await onCreatePersona?.(trimmed);
      setCreating(false);
      setNewName('');
      if (persona?.id) setDetailPersonaId(persona.id);
    } finally {
      setBusy(false);
    }
  };

  if (detailPersona) {
    return (
      <>
        <div className="music-playlist-backdrop" onClick={onClose} aria-hidden="true" />
        <div
          className="music-playlist-sheet music-persona-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={detailPersona.name}
        >
          <PersonaDetailPane
            persona={detailPersona}
            playlists={playlists}
            allTracks={allTracks}
            appendMode={appendMode}
            onBack={() => setDetailPersonaId(null)}
            onPlay={(p) => playAndClose([p])}
            onClose={onClose}
            onRemoveTrack={onRemoveTrack}
            onAddTracks={onAddTracks}
            onSuggest={onSuggestTracks}
            onPlayLibraryTrack={onPlayLibraryTrack}
            onAddSuggestedTrack={onAddSuggestedTrack}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="music-playlist-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="music-playlist-sheet music-persona-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Personalities"
      >
        <div className="music-playlist-sheet-header">
          <div>
            <h2>Personalities</h2>
            <p>
              {personas.length === 0
                ? 'Create a personality for each listener in this account'
                : `${personas.length} personality${personas.length === 1 ? '' : 'ies'}`}
            </p>
          </div>
          <div className="music-persona-header-actions">
            <button
              type="button"
              className="music-persona-create-btn"
              onClick={() => setCreating(true)}
              aria-label="Create personality"
              title="Create personality"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <button type="button" className="music-playlist-sheet-done" onClick={onClose}>
              Done
            </button>
          </div>
        </div>

        {creating && (
          <div className="music-persona-create-form">
            <input
              autoFocus
              value={newName}
              maxLength={60}
              placeholder="Name (e.g. Kids, Mom, Workout)"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCreate();
                if (e.key === 'Escape') { setCreating(false); setNewName(''); }
              }}
              aria-label="New personality name"
            />
            <button type="button" onClick={submitCreate} disabled={busy || !newName.trim()}>
              Create
            </button>
            <button
              type="button"
              className="music-persona-create-cancel"
              onClick={() => { setCreating(false); setNewName(''); }}
            >
              Cancel
            </button>
          </div>
        )}

        {personas.length === 0 ? (
          <div className="music-playlist-empty">
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            <p>
              Create a personality, add playlists or tracks, then hit play.
              Listening while a personality is active teaches its mix.
            </p>
          </div>
        ) : (
          <ul className="music-persona-list">
            {personas.map((persona) => (
              <PersonaRow
                key={persona.id}
                persona={persona}
                isActive={activePersonaIds.includes(persona.id)}
                appendMode={appendMode}
                onPlay={(p) => playAndClose([p])}
                onRename={onRenamePersona}
                onOpenDetail={(p) => setDetailPersonaId(p.id)}
                onDelete={onDeletePersona}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default PersonaModal;
